import { css } from "styled-components";

/**
 * Micro-interaction の共通アニメーション。
 *
 * `MicroInteraction` コンポーネントだけでなく、styled-components で組まれた
 * ローカル要素（例: `PressToConfirm` の確定ラベル）からも同じ keyframes を
 * 参照できるよう、css helper として切り出している。
 *
 * 使い方:
 *
 * ```ts
 * import { microBounce } from "../micro-interaction/animations";
 *
 * const ThanksLabel = styled.div`
 *   ${microBounce}
 * `;
 * ```
 */

/** ふわっと弾むように現れる。長押し確定など、達成感を強調したい場面に。 */
export const microBounce = css`
  animation: micro-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  @keyframes micro-bounce {
    0% {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    50% {
      transform: scale(1.05) translateY(-2px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

/** ゆるやかに拡大しながら現れる。汎用的なフィードバック向け。 */
export const microScale = css`
  animation: micro-scale 0.3s ease-out;
  @keyframes micro-scale {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

/** opacity だけで現れる、最も控えめなフィードバック。 */
export const microFade = css`
  animation: micro-fade 0.3s ease-out;
  @keyframes micro-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/** 軽くスライドしながら現れる（slide も挙動は fade と同じ keyframes を流用）。 */
export const microSlide = microFade;

/** 横揺れ。エラーや警告など「注意してほしい」場面の強調用。 */
export const microWobble = css`
  animation: micro-wobble 0.8s ease-in-out both;
  @keyframes micro-wobble {
    0%,
    100% {
      transform: translateX(0);
      transform-origin: 50% 50%;
    }
    15% {
      transform: translateX(-30px) rotate(-6deg);
    }
    30% {
      transform: translateX(15px) rotate(6deg);
    }
    45% {
      transform: translateX(-15px) rotate(-3.6deg);
    }
    60% {
      transform: translateX(9px) rotate(2.4deg);
    }
    75% {
      transform: translateX(-6px) rotate(-1.2deg);
    }
  }
`;

export type MicroAnimationType =
  | "bounce"
  | "scale"
  | "fade"
  | "slide"
  | "wobble";

/** animationType 文字列から該当する css helper を返すルックアップ。 */
export const microAnimationFor = (type: MicroAnimationType) => {
  switch (type) {
    case "bounce":
      return microBounce;
    case "scale":
      return microScale;
    case "wobble":
      return microWobble;
    case "fade":
    case "slide":
    default:
      return microFade;
  }
};
